import { useCallback } from 'react';
import * as R from 'remeda';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import * as z from 'zod';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import { Header } from '@rossum/components/header/Header';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { MuiColorInput } from '@rossum/ui';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { SettingsBreadcrumbs } from '../../containers/Settings/SettingsBreadcrumbs';
import { updateOrganization } from '../../redux/modules/organization/actions';
import { safeOrganizationSelector } from '../../redux/modules/organization/selectors';
import { Organization } from '../../types/organization';
import { organizationBranding } from '../client-config/organization/branding';
import { FileUploadComponent } from './FileUploadComponent';

const appearanceFormSchema = z.object({
  fontFamily: z.string(),
  primaryColor: z.string(),
  defaultTheme: z.enum(['light', 'dark', 'coupa', 'none']),
  userAvatar: z.string(),
  logo: z.string(),
  displayRossumBranding: z.boolean(),
});

const fontFamilyOptions = [
  'Default (Inter)',
  'Nata Sans',
  'Helvetica,sans-serif',
  'Arial,sans-serif',
  'Arial Black,sans-serif',
  'Verdana,sans-serif',
  'Tahoma,sans-serif',
  'Trebuchet MS,Sans-serif',
  'Impact,sans-serif',
  'Gill Sans,sans-serif',
  'Times New Roman,serif',
  'Georgia,serif',
  'Palatino,serif',
  'Baskerville,serif',
  'Andalé Mono,monospace',
  'Courier,monospace',
  'Lucida,monospace',
  'Monaco,monospace',
  'Bradley Hand,cursive',
  'Brush Script MT,cursive',
  'Luminari,fantasy',
  'Comic Sans MS,cursive',
  'Roboto, Roboto, monospace',
];

type AppearanceForm = z.infer<typeof appearanceFormSchema>;

export const AppearancePage = () => {
  const intl = useIntl();

  // TODO: Refactor so you implicitly get correct organization
  const organization = useSelector(safeOrganizationSelector);

  const defaultValues: AppearanceForm = {
    fontFamily:
      organizationBranding.fontFamily(organization?.uiSettings) ??
      'Default (Inter)',
    primaryColor:
      organizationBranding.primaryColor(organization?.uiSettings) ?? '',
    defaultTheme:
      organizationBranding.defaultTheme(organization?.uiSettings ?? {}) ??
      'none',
    logo: organizationBranding.customLogoImage(organization?.uiSettings) ?? '',
    displayRossumBranding: organizationBranding.displayRossumBranding(
      organization?.uiSettings
    ),
    userAvatar:
      organizationBranding.customUserAvatarImage(organization?.uiSettings) ??
      '',
  };

  const defaultThemeOptions = [
    { value: 'none', label: 'None' },
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    {
      value: 'coupa',
      label: intl.formatMessage({
        id: 'features.personalSettings.theme.coupa',
      }),
    },
  ];

  const { control, handleSubmit } = useForm({
    resolver: zodResolver(appearanceFormSchema),
    defaultValues,
  });

  const dispatch = useDispatch();

  const onSubmit = useCallback(
    (form: AppearanceForm) => {
      if (!organization) {
        return;
      }

      const uiSettings = R.pipe(
        organization.uiSettings,
        organizationBranding.setCustomLogoImage(form.logo ? form.logo : null),
        organizationBranding.setFontFamily(
          form.fontFamily && form.fontFamily !== 'Default (Inter)'
            ? form.fontFamily
            : null
        ),
        organizationBranding.setPrimaryColor(
          form.primaryColor ? form.primaryColor : null
        ),
        organizationBranding.setDefaultTheme(
          form.defaultTheme && form.defaultTheme !== 'none'
            ? form.defaultTheme
            : null
        ),
        organizationBranding.setDisplayRossumBranding(
          form.displayRossumBranding
        ),
        organizationBranding.setCustomUserAvatarImage(
          form.userAvatar ? form.userAvatar : null
        )
      );

      dispatch(
        updateOrganization({
          ...organization,
          // TODO: Remove once UI settings are unknown
          uiSettings: uiSettings as Organization['uiSettings'],
        })
      );
    },
    [dispatch, organization]
  );

  return (
    <PageLayout
      renderHeader={params => (
        <Header
          scrollableDivRef={params.scrollableDivRef}
          title={intl.formatMessage({
            id: 'containers.settings.appearance.header.title',
          })}
          description={intl.formatMessage({
            id: 'containers.settings.appearance.header.description',
          })}
          breadcrumbs={
            <SettingsBreadcrumbs
              breadcrumbs={[
                {
                  label: intl.formatMessage({
                    id: 'containers.settings.appearance.header.title',
                  }),
                },
              ]}
            />
          }
          buttons={[
            <Button
              key="save-appearance"
              data-cy="save-appearance-button"
              variant="contained"
              type="submit"
              form="appearance-form"
            >
              {intl.formatMessage({
                id: 'containers.settings.appearance.save',
              })}
            </Button>,
          ]}
        />
      )}
    >
      <Stack p={4}>
        <ContentGroup
          title={intl.formatMessage({
            id: 'containers.settings.appearance.theme.title',
          })}
          description={intl.formatMessage({
            id: 'containers.settings.appearance.theme.description',
          })}
        >
          <Stack
            component="form"
            id="appearance-form"
            spacing={4}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Controller
              name="logo"
              control={control}
              render={({ field }) => (
                <FormControl>
                  <FormLabel id="logo-label">
                    <Typography variant="caption" color="text.secondary">
                      Brand logo upload
                    </Typography>
                  </FormLabel>
                  <FileUploadComponent {...field} />
                </FormControl>
              )}
            />
            <Controller
              name="userAvatar"
              control={control}
              render={({ field }) => (
                <FormControl>
                  <FormLabel id="user-avatar-label">
                    <Typography variant="caption" color="text.secondary">
                      User avatar upload
                    </Typography>
                  </FormLabel>
                  <FileUploadComponent {...field} />
                </FormControl>
              )}
            />
            <Controller
              name="fontFamily"
              control={control}
              render={({ field }) => (
                <TextField select size="small" label="Font family" {...field}>
                  {fontFamilyOptions.map(option => (
                    <MenuItem
                      key={option}
                      value={option}
                      sx={{ fontFamily: option }}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="primaryColor"
              control={control}
              render={({ field }) => (
                <MuiColorInput
                  label="Primary color"
                  format="hex"
                  size="small"
                  {...field}
                />
              )}
            />
            <Controller
              name="defaultTheme"
              control={control}
              render={({ field }) => (
                <FormControl>
                  <FormLabel id="default-theme">
                    <Typography variant="caption" color="text.secondary">
                      Default theme
                    </Typography>
                  </FormLabel>
                  <RadioGroup {...field}>
                    {defaultThemeOptions.map(option => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio size="small" sx={{ p: 0.5 }} />}
                        sx={{ ml: 0, mr: 0, mb: 0 }}
                        label={
                          <Typography variant="body2">
                            {option.label}
                          </Typography>
                        }
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            />
            <Controller
              name="displayRossumBranding"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  label="Display Rossum branding"
                  control={<Checkbox checked={field.value} {...field} />}
                />
              )}
            />
          </Stack>
        </ContentGroup>
      </Stack>
    </PageLayout>
  );
};
